import React, {ReactNode} from "react";
import {Box, IconButton, Stack, Tooltip} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {NodeViewWrapper} from "@tiptap/react";
import type {NodeViewProps} from "@tiptap/react";
import type {Node as ProseMirrorNode} from "@tiptap/pm/model";
import {NodeSelection} from "@tiptap/pm/state";
import {useTranslation} from "react-i18next";
import type {PrintoutBlockType} from "../blockRegistry";

interface PrintoutBlockShellProps extends NodeViewProps {
    active?: boolean;
    activateOnContentSelection?: boolean;
    allowEditableContent?: boolean;
    blockType: PrintoutBlockType;
    children: ReactNode;
    dataAttributes?: Record<string, string | undefined>;
    editableContentSelector?: string;
    icon: ReactNode;
    label: string;
    menuActive?: boolean;
    quickActions?: ReactNode;
}

export interface PrintoutNodeSelectionState {
    /** A descendant block or node view owns the current selection. */
    nestedSelectionOwner: boolean;
    /** This exact node is the current node selection. */
    nodeSelected: boolean;
    /** The selection sits strictly inside this node. */
    selectionInside: boolean;
}

const IDLE_PRINTOUT_NODE_SELECTION: PrintoutNodeSelectionState = {
    nestedSelectionOwner: false,
    nodeSelected: false,
    selectionInside: false,
};

const hasNestedDraggableSelectionOwner = (
    editor: NodeViewProps["editor"],
    position: number,
    currentNode: ProseMirrorNode,
): boolean => {
    const {selection} = editor.state;

    // Inline variable node views and selected descendant blocks own their chrome.
    if (selection instanceof NodeSelection) return true;

    let nestedOwner = false;

    // Printout blocks are draggable. A text or cell selection contained by one of
    // those descendants belongs to that block rather than to its outer container.
    currentNode.descendants((node, relativePosition) => {
        if (nestedOwner) return false;
        if (node.type.spec.draggable !== true) return true;

        const nodePosition = position + 1 + relativePosition;

        if (selection.from > nodePosition && selection.to < nodePosition + node.nodeSize) {
            nestedOwner = true;
            return false;
        }

        return true;
    });

    return nestedOwner;
};

export const readPrintoutNodeSelection = (
    editor: NodeViewProps["editor"],
    getPos: NodeViewProps["getPos"],
    expectedNodeType: string,
): PrintoutNodeSelectionState => {
    if (editor.isDestroyed) return IDLE_PRINTOUT_NODE_SELECTION;

    let position: number | undefined;

    try {
        const resolvedPosition = getPos();
        position = typeof resolvedPosition === "number" ? resolvedPosition : undefined;
    } catch {
        // A transaction can detach a node view before React unmounts it.
        return IDLE_PRINTOUT_NODE_SELECTION;
    }

    if (typeof position !== "number") return IDLE_PRINTOUT_NODE_SELECTION;

    const {doc, selection} = editor.state;

    if (position < 0 || position >= doc.content.size) return IDLE_PRINTOUT_NODE_SELECTION;

    // Transaction events run before React receives updated node-view props. Resolve
    // the live node so structural edits never scan with stale document bounds.
    const currentNode = doc.nodeAt(position);

    if (!currentNode || currentNode.type.name !== expectedNodeType) {
        return IDLE_PRINTOUT_NODE_SELECTION;
    }

    const inside = selection.from > position && selection.to < position + currentNode.nodeSize;

    return {
        nestedSelectionOwner: inside && hasNestedDraggableSelectionOwner(editor, position, currentNode),
        nodeSelected: selection instanceof NodeSelection && selection.from === position,
        selectionInside: inside,
    };
};

export const printoutSelectionActivatesNode = (
    selection: PrintoutNodeSelectionState,
    includeContentSelection: boolean,
): boolean => (
    selection.nodeSelected
    || (
        includeContentSelection
        && selection.selectionInside
        && !selection.nestedSelectionOwner
    )
);

// Tiptap's `selected` prop is a containment flag: it stays true while an ancestor node
// selection covers this node, so it does not flip when the selection moves between an
// ancestor and its descendant, and the memoized node view never re-renders. Deriving
// selection chrome from that prop plus a render-time read of `editor.state` leaves the
// two halves stale against each other. Own one state object here instead, and refresh it
// from the editor so every source of "am I active" invalidates together.
export const usePrintoutNodeSelection = (
    editor: NodeViewProps["editor"],
    getPos: NodeViewProps["getPos"],
    nodeType: string,
): PrintoutNodeSelectionState => {
    const [state, setState] = React.useState<PrintoutNodeSelectionState>(
        () => readPrintoutNodeSelection(editor, getPos, nodeType),
    );

    const sync = React.useCallback(() => {
        setState((current) => {
            const next = readPrintoutNodeSelection(editor, getPos, nodeType);

            return current.nestedSelectionOwner === next.nestedSelectionOwner
                && current.nodeSelected === next.nodeSelected
                && current.selectionInside === next.selectionInside
                ? current
                : next;
        });
    }, [editor, getPos, nodeType]);

    React.useEffect(() => {
        sync();
        editor.on("selectionUpdate", sync);
        editor.on("transaction", sync);

        return () => {
            editor.off("selectionUpdate", sync);
            editor.off("transaction", sync);
        };
    }, [editor, sync]);

    return state;
};

const PRINTOUT_PAGE_SELECTOR = ".printout-tiptap-editor";

const MENU_PAGE_INSET_PX = 4;

export const usePrintoutFloatingMenuPlacement = <T extends HTMLElement = HTMLDivElement>() => {
    const menuRef = React.useRef<T | null>(null);

    // Re-measure after every render because pagination can move the selected block.
    React.useLayoutEffect(() => {
        if (!menuRef.current) return;

        const placeMenu = () => {
            const menu = menuRef.current;
            const page = menu?.closest(PRINTOUT_PAGE_SELECTOR) as HTMLElement | null;

            if (!menu || !page) return;

            menu.style.removeProperty("--printout-menu-shift-x");
            menu.removeAttribute("data-printout-menu-placement");

            const pageRect = page.getBoundingClientRect();
            const menuRect = menu.getBoundingClientRect();
            const scale = page.offsetWidth ? pageRect.width / page.offsetWidth : 1;
            const overflowRight = menuRect.right - (pageRect.right - MENU_PAGE_INSET_PX);
            const overflowLeft = (pageRect.left + MENU_PAGE_INSET_PX) - menuRect.left;
            // For an over-wide menu, anchor the left edge instead of oscillating between edges.
            const shift = overflowLeft > 0 ? overflowLeft : Math.min(0, -overflowRight);

            if (shift) {
                menu.style.setProperty("--printout-menu-shift-x", `${shift / (scale || 1)}px`);
            }

            if (menuRect.top < pageRect.top + MENU_PAGE_INSET_PX) {
                menu.setAttribute("data-printout-menu-placement", "below");
            }
        };

        placeMenu();
        window.addEventListener("resize", placeMenu);

        return () => window.removeEventListener("resize", placeMenu);
    });

    return menuRef;
};

const PrintoutBlockShell = ({
    active,
    activateOnContentSelection = false,
    allowEditableContent = false,
    blockType,
    children,
    dataAttributes,
    deleteNode,
    editableContentSelector,
    editor,
    getPos,
    icon,
    label,
    menuActive,
    node,
    quickActions,
}: PrintoutBlockShellProps) => {
    const {t} = useTranslation();
    const [dragging, setDragging] = React.useState(false);
    const menuRef = usePrintoutFloatingMenuPlacement();
    const editable = editor.isEditable;
    const nodeSelection = usePrintoutNodeSelection(editor, getPos, node.type.name);
    const blockIsActive = printoutSelectionActivatesNode(
        nodeSelection,
        activateOnContentSelection,
    ) || active;
    // Hide selection chrome while native dragging is active.
    const blockMenuIsActive = (menuActive ?? blockIsActive) && !dragging;
    const className = [
        "printout-block-shell",
        blockIsActive ? "is-selected" : "",
    ].filter(Boolean).join(" ");

    const selectBlockAtCurrentPosition = (focusEditor = false) => {
        const position = getPos();
        if (typeof position !== "number") return false;

        editor.commands.setNodeSelection(position);
        if (focusEditor) editor.view.focus();
        return true;
    };

    const selectBlock = (event: React.MouseEvent<HTMLElement>) => {
        if (!editable) return;
        const target = event.target as HTMLElement;

        // React portal events bubble here; only editor DOM should alter ProseMirror selection.
        if (!editor.view.dom.contains(target)) return;

        if (target.closest("[data-printout-block-action]")) return;

        // Nested node views own their selection; the shell must not replace it with a block
        // selection. Every selectable node view carries the marker — this shell included — so
        // the check is "the nearest marked ancestor is not me", not "a marker exists".
        const selectableNode = target.closest("[data-printout-selectable-node]");

        if (selectableNode && selectableNode !== event.currentTarget) return;

        if (editableContentSelector && target.closest(editableContentSelector)) return;

        event.preventDefault();
        selectBlockAtCurrentPosition(true);
    };

    const keepEditorSelection = (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
    };

    const duplicateBlockBelow = () => {
        const position = getPos();
        if (typeof position !== "number") return;

        const insertPosition = position + node.nodeSize;
        const duplicateNode = editor.schema.nodeFromJSON(node.toJSON());
        const transaction = editor.state.tr.insert(insertPosition, duplicateNode);

        editor.view.dispatch(
            transaction
                .setSelection(NodeSelection.create(transaction.doc, insertPosition))
                .scrollIntoView(),
        );
        editor.view.focus();
    };

    return (
        <NodeViewWrapper
            as="div"
            className={className}
            contentEditable={allowEditableContent ? undefined : false}
            {...dataAttributes}
            data-printout-block-type={blockType}
            // Prevent an enclosing block shell from stealing this node selection.
            data-printout-selectable-node=""
            onMouseDown={selectBlock}
        >
            {editable && (
                <Box
                    aria-label={t("Drag block")}
                    className="printout-block-drag-handle printout-block-gutter-handle"
                    contentEditable={false}
                    data-drag-handle
                    data-printout-block-action
                    draggable
                    onClick={() => {
                        selectBlockAtCurrentPosition(true);
                    }}
                    onDragEnd={() => setDragging(false)}
                    onDragStart={() => {
                        selectBlockAtCurrentPosition();
                        setDragging(true);
                    }}
                    role="button"
                >
                    <DragIndicatorIcon fontSize="small"/>
                </Box>
            )}
            {editable && blockMenuIsActive && (
                <Stack
                    alignItems="center"
                    className="printout-block-menu"
                    contentEditable={false}
                    data-printout-block-action
                    direction="row"
                    gap={0.25}
                    ref={menuRef}
                >
                    <Box
                        className="printout-block-name-label"
                        component="span"
                        onMouseDown={keepEditorSelection}
                    >
                        <Box className="printout-block-name-label-icon" component="span">
                            {icon}
                        </Box>
                        {t(label)}
                    </Box>
                    {quickActions && (
                        <Box
                            className="printout-block-quick-actions"
                            component="span"
                            onMouseDown={keepEditorSelection}
                        >
                            {quickActions}
                        </Box>
                    )}
                    <Tooltip title={t("Duplicate block")}>
                        <IconButton
                            aria-label={t("Duplicate block")}
                            className="printout-block-menu-icon"
                            onClick={duplicateBlockBelow}
                            onMouseDown={keepEditorSelection}
                            size="small"
                        >
                            <ContentCopyIcon fontSize="small"/>
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t("Delete block")}>
                        <IconButton
                            aria-label={t("Delete block")}
                            className="printout-block-menu-icon"
                            onClick={deleteNode}
                            onMouseDown={keepEditorSelection}
                            size="small"
                        >
                            <DeleteOutlineIcon fontSize="small"/>
                        </IconButton>
                    </Tooltip>
                </Stack>
            )}
            {editable && (
                <Box
                    aria-hidden="true"
                    className="printout-block-dash"
                    contentEditable={false}
                    sx={{
                        inset: "-4px",
                        position: "absolute",
                    }}
                >
                    <svg
                        fill="none"
                        style={{height: "100%", inset: 0, overflow: "visible", position: "absolute", width: "100%"}}
                    >
                        <rect
                            rx="4"
                            ry="4"
                            stroke="currentColor"
                            strokeDasharray="6 6"
                            strokeLinecap="round"
                            strokeWidth="1.5"
                            style={{height: "calc(100% - 2px)", width: "calc(100% - 2px)"}}
                            x="1"
                            y="1"
                        />
                    </svg>
                </Box>
            )}
            <Box className="printout-block-content">
                {children}
            </Box>
        </NodeViewWrapper>
    );
};

export default PrintoutBlockShell;
